import { useMemo } from 'react'
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { DailyDashboardStatDto } from '@api/types/dashboard'
import { CHART_COLORS } from './chartConfig'

interface UserActivityChartProps {
  data?: DailyDashboardStatDto[]
  isLoading?: boolean
}

export function UserActivityChart({ data, isLoading }: UserActivityChartProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const chartData = useMemo(() => {
    if (!data) return []
    return data.map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      activeUsers: item.activeUsers,
      newUsers: item.newUsers,
    }))
  }, [data])

  const gridColor = isDark ? CHART_COLORS.gridDark : CHART_COLORS.grid
  const axisColor = isDark ? CHART_COLORS.axisDark : CHART_COLORS.axis

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        User Activity
      </Typography>
      {isLoading ? (
        <Box sx={{ height: 300 }}>
          <Skeleton variant="rectangular" height="100%" />
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={{ fill: axisColor, fontSize: 12 }} />
            <YAxis tick={{ fill: axisColor, fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="activeUsers"
              name="Active Users"
              stroke={CHART_COLORS.activeUsers}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="newUsers"
              name="New Users"
              stroke={CHART_COLORS.newUsers}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Paper>
  )
}
